// libraries.
import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { monserratSemiBold, robotoRegular, scarpaGray500 } from "@habitech/shared";

// interfaces.
import { RawRuleItem } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// styles.
import { StyledRulesModalForm, StyledPromoSuccess } from "../styles";

interface Props {
  show: boolean;
  subRutaName: string;
  contenders: RawRuleItem[];
  ganadores: RawRuleItem[];
  onConfirm: () => void;
  onClose: () => void;
}

const ANIMATION_DURATION_MS = 2600;

const text = { base: robotoRegular, semi: monserratSemiBold };

// ─── Compact rule row shared between the two lists ────────────────────────────

interface PromoteRuleRowProps {
  rule: RawRuleItem;
  isLast: boolean;
  variant: "winner" | "contender";
}

const PromoteRuleRow = ({ rule, isLast, variant }: PromoteRuleRowProps) => {
  const isWinner = variant === "winner";
  const dividerColor = isWinner ? "#f3f4f6" : "#fef3c7";
  const borderBottom = isLast ? "none" : `1px solid ${dividerColor}`;
  return (
    <div
      style={{
        padding: "8px 14px",
        borderBottom,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: isWinner ? "#fafafa" : "#fffdf5",
        opacity: isWinner ? 0.65 : 1,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: isWinner ? "#e5e7eb" : "#fef3c7",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          fontWeight: 700,
          color: isWinner ? "#6b7280" : "#b45309",
          flexShrink: 0,
        }}
      >
        {rule.rule.priority}
      </span>
      <span style={{ fontFamily: text.base, fontSize: 13, color: "#333" }}>
        {rule.rule.name}
      </span>
    </div>
  );
};

export const PromoteModal = ({
  show,
  subRutaName,
  contenders,
  ganadores,
  onConfirm,
  onClose,
}: Props) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const totalRules = contenders.length + ganadores.length;

  // Reset animation state when modal re-opens.
  useEffect(() => {
    if (show) {
      setShowSuccess(false);
      setFadingOut(false);
    }
  }, [show]);

  const handleConfirm = () => {
    onConfirm();
    setShowSuccess(true);

    // Start fade-out shortly before closing.
    setTimeout(() => setFadingOut(true), ANIMATION_DURATION_MS - 400);
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, ANIMATION_DURATION_MS);
  };

  return (
    <Modal
      show={show}
      onHide={showSuccess ? undefined : onClose}
      centered
      size="lg"
      backdrop={showSuccess ? "static" : true}
      keyboard={!showSuccess}
    >
      {showSuccess ? (
        <>
          <Modal.Body style={{ padding: 0 }}>
            <StyledPromoSuccess className={fadingOut ? "fading-out" : ""}>
              <div className="success-trophy">🏆</div>
              <div className="success-ko">¡KO! NUEVO WINNER</div>
              <div className="success-subtitle">{subRutaName}</div>
              <div className="success-name">
                Los Contenders han tomado el cinturón
              </div>
              <div className="success-gloves">
                <span className="glove-left">🥊</span>
                <span className="glove-right">🥊</span>
              </div>
            </StyledPromoSuccess>
          </Modal.Body>
        </>
      ) : (
        <>
          <Modal.Header
            closeButton
            style={{ borderBottom: "2px solid #b45309", background: "#fffdf5" }}
          >
            <Modal.Title style={{ fontFamily: text.semi, fontWeight: 700, fontSize: 16 }}>
              🥊 Promover Contender a Winner
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ padding: "24px" }}>
            <StyledRulesModalForm>
              {/* ── Descripción ─────────────────────────────────────────────── */}
              <p
                style={{
                  fontFamily: text.base,
                  fontSize: 14,
                  lineHeight: "22px",
                  color: "#1a1a1a",
                  marginBottom: 20,
                }}
              >
                Estás a punto de promover los experimentos de{" "}
                <strong style={{ fontFamily: text.semi }}>{subRutaName}</strong> a
                producción. Las reglas actuales del Winner pasarán a estado{" "}
                <strong style={{ fontFamily: text.semi }}>Desactivado</strong> y los
                Contenders se convertirán en el nuevo Winner.
              </p>

              {/* ── Grid antes → después ─────────────────────────────────────── */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 40px 1fr",
                  gap: 12,
                  alignItems: "start",
                }}
              >
                {/* Winner actual */}
                <div>
                  <p
                    style={{
                      fontFamily: text.semi,
                      fontSize: 11,
                      color: scarpaGray500,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 8,
                    }}
                  >
                    🏆 Winner actual
                  </p>
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                    {ganadores.length === 0 ? (
                      <p style={{ padding: "12px 14px", fontFamily: text.base, fontSize: 13, color: scarpaGray500, margin: 0 }}>
                        Sin reglas activas
                      </p>
                    ) : (
                      ganadores.map((r, i) => (
                        <PromoteRuleRow
                          key={r.rule_id}
                          rule={r}
                          isLast={i === ganadores.length - 1}
                          variant="winner"
                        />
                      ))
                    )}
                  </div>
                  <p style={{ fontFamily: text.base, fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                    Pasarán a Desactivado (state_route → 0)
                  </p>
                </div>

                {/* Flecha */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingTop: 32, fontSize: 20, color: scarpaGray500 }}>
                  →
                </div>

                {/* Contenders */}
                <div>
                  <p
                    style={{
                      fontFamily: text.semi,
                      fontSize: 11,
                      color: "#b45309",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 8,
                    }}
                  >
                    🥊 Contender (nuevo Winner)
                  </p>
                  <div style={{ border: "1px solid #fde68a", borderRadius: 8, overflow: "hidden" }}>
                    {contenders.length === 0 ? (
                      <p style={{ padding: "12px 14px", fontFamily: text.base, fontSize: 13, color: scarpaGray500, margin: 0 }}>
                        Sin experimentos
                      </p>
                    ) : (
                      contenders.map((r, i) => (
                        <PromoteRuleRow
                          key={r.rule_id}
                          rule={r}
                          isLast={i === contenders.length - 1}
                          variant="contender"
                        />
                      ))
                    )}
                  </div>
                  <p style={{ fontFamily: text.base, fontSize: 11, color: "#b45309", marginTop: 6 }}>
                    Serán el nuevo Winner (state_route → 1)
                  </p>
                </div>
              </div>

              {/* ── Advertencia ─────────────────────────────────────────────── */}
              <div
                style={{
                  marginTop: 20,
                  padding: "12px 16px",
                  background: "#fef9e7",
                  borderRadius: 8,
                  border: "1px solid #fde68a",
                }}
              >
                <p style={{ fontFamily: text.base, fontSize: 13, color: "#92400e", margin: 0 }}>
                  ⚠️ Esta acción actualizará el <code>state_route</code> de{" "}
                  <strong style={{ fontFamily: text.semi }}>{totalRules}</strong> regla
                  {totalRules !== 1 ? "s" : ""} y no puede deshacerse desde aquí. Usa
                  el historial para revisar cambios.
                </p>
              </div>
            </StyledRulesModalForm>
          </Modal.Body>

          <Modal.Footer style={{ borderTop: "1px solid #fde68a", background: "#fffdf5" }}>
            <Button className="btn-habi-white" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={contenders.length === 0}
              style={{
                background: "#b45309",
                borderColor: "#b45309",
                fontFamily: robotoRegular,
                fontSize: 14,
              }}
            >
              🏆 Confirmar promoción
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};
